import { Card, CardContent, Divider, VStack, HStack } from '@components/base/layout';
import { Text } from '@components/base/display';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const colors = ['default', 'light', 'dark'] as const;
const spacings = [0, 2, 4, 6, 8] as const;

function DividerShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="divider">
          <div className="p-4 border border-dashed border-[#D4D1CC] rounded">
            {playgroundProps.orientation === 'horizontal' ? (
              <VStack gap={0}>
                <Text size="sm">Content above</Text>
                <Divider
                  orientation="horizontal"
                  spacing={4}
                />
                <Text size="sm">Content below</Text>
              </VStack>
            ) : (
              <HStack gap={0} className="h-16 items-center">
                <Text size="sm">Left content</Text>
                <Divider
                  orientation="vertical"
                  spacing={4}
                />
                <Text size="sm">Right content</Text>
              </HStack>
            )}
          </div>
      </ShowcasePreviewSection>

      {/* Orientations */}
      <ShowcaseDemoSection
        title="Orientations"
        description="Dividers can be horizontal (full width) or vertical (full height)"
       
      >
          <HStack gap={8} wrap="wrap" align="start">
            <VStack gap={2} className="w-64">
              <Text size="sm" weight="semibold" color="muted">Horizontal</Text>
              <Card>
                <CardContent>
                  <VStack gap={0}>
                    <Text size="sm">Section 1</Text>
                    <Divider orientation="horizontal" spacing={3} />
                    <Text size="sm">Section 2</Text>
                    <Divider orientation="horizontal" spacing={3} />
                    <Text size="sm">Section 3</Text>
                  </VStack>
                </CardContent>
              </Card>
            </VStack>
            <VStack gap={2}>
              <Text size="sm" weight="semibold" color="muted">Vertical</Text>
              <Card>
                <CardContent>
                  <HStack gap={0} className="h-20 items-center">
                    <Text size="sm" className="px-2">Home</Text>
                    <Divider orientation="vertical" spacing={2} />
                    <Text size="sm" className="px-2">Policies</Text>
                    <Divider orientation="vertical" spacing={2} />
                    <Text size="sm" className="px-2">Claims</Text>
                    <Divider orientation="vertical" spacing={2} />
                    <Text size="sm" className="px-2">Support</Text>
                  </HStack>
                </CardContent>
              </Card>
            </VStack>
          </HStack>
      </ShowcaseDemoSection>

      {/* Color Variants */}
      <ShowcaseDemoSection
        title="Color Variants"
        description="Three color intensities: light, default, and dark"
       
      >
          <VStack gap={4} className="max-w-md">
            {colors.map((color) => (
              <VStack key={color} gap={1}>
                <Text size="xs" color="muted" className="capitalize">{color}</Text>
                <div className="p-3 border border-[#E8E6E3] rounded">
                  <VStack gap={0}>
                    <Text size="sm">Above</Text>
                    <Divider color={color} spacing={2} />
                    <Text size="sm">Below</Text>
                  </VStack>
                </div>
              </VStack>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Spacing Options */}
      <ShowcaseDemoSection
        title="Spacing Options"
        description="Control the margin around the divider (0-8 maps to 0-32px)"
       
      >
          <VStack gap={4} className="max-w-md">
            {spacings.map((spacing) => (
              <VStack key={spacing} gap={1}>
                <Text size="xs" color="muted">spacing={spacing} ({spacing * 4}px)</Text>
                <div className="p-3 border border-dashed border-[#D4D1CC] rounded">
                  <VStack gap={0}>
                    <div className="bg-[#E8E6E3] p-2 rounded text-xs">Content</div>
                    <Divider spacing={spacing} />
                    <div className="bg-[#E8E6E3] p-2 rounded text-xs">Content</div>
                  </VStack>
                </div>
              </VStack>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* With Text Labels */}
      <ShowcaseDemoSection
        title="With Text Labels"
        description="Horizontal dividers can include centered or positioned text labels"
       
      >
          <VStack gap={6} className="max-w-md">
            <VStack gap={1}>
              <Text size="xs" color="muted">Center (default)</Text>
              <Divider label="OR" />
            </VStack>
            <VStack gap={1}>
              <Text size="xs" color="muted">Start position</Text>
              <Divider label="Section A" labelPosition="start" />
            </VStack>
            <VStack gap={1}>
              <Text size="xs" color="muted">End position</Text>
              <Divider label="Continue" labelPosition="end" />
            </VStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* In Context - Login Form */}
      <ShowcaseDemoSection
        title="In Context: Login Form"
        description="Common pattern using labeled divider for alternative login options"
       
      >
          <Card className="max-w-sm mx-auto">
            <CardContent>
              <VStack gap={4}>
                <Text size="lg" weight="semibold" className="text-center">Sign In</Text>
                <div className="w-full h-10 bg-[#E8E6E3] rounded flex items-center justify-center">
                  <Text size="sm" color="muted">Email Input</Text>
                </div>
                <div className="w-full h-10 bg-[#E8E6E3] rounded flex items-center justify-center">
                  <Text size="sm" color="muted">Password Input</Text>
                </div>
                <div className="w-full h-10 bg-[#1C1917] rounded flex items-center justify-center">
                  <Text size="sm" className="text-white">Sign In</Text>
                </div>
                <Divider label="or continue with" spacing={2} />
                <HStack gap={2} justify="center">
                  <div className="w-20 h-10 bg-[#E8E6E3] rounded flex items-center justify-center">
                    <Text size="xs" color="muted">Google</Text>
                  </div>
                  <div className="w-20 h-10 bg-[#E8E6E3] rounded flex items-center justify-center">
                    <Text size="xs" color="muted">Apple</Text>
                  </div>
                </HStack>
              </VStack>
            </CardContent>
          </Card>
      </ShowcaseDemoSection>

      {/* In Context - Between Cards */}
      <ShowcaseDemoSection
        title="In Context: Between Sections"
        description="Using dividers to separate content sections in a list or detail view"
       
      >
          <Card className="max-w-md">
            <CardContent>
              <VStack gap={0}>
                <VStack gap={1}>
                  <Text size="sm" weight="semibold">Policy Holder</Text>
                  <Text size="sm" color="muted">John Smith</Text>
                </VStack>
                <Divider spacing={4} />
                <VStack gap={1}>
                  <Text size="sm" weight="semibold">Policy Number</Text>
                  <Text size="sm" color="muted">POL-2026-001234</Text>
                </VStack>
                <Divider spacing={4} />
                <VStack gap={1}>
                  <Text size="sm" weight="semibold">Coverage Type</Text>
                  <Text size="sm" color="muted">Comprehensive Auto Insurance</Text>
                </VStack>
                <Divider spacing={4} />
                <VStack gap={1}>
                  <Text size="sm" weight="semibold">Monthly Premium</Text>
                  <Text size="sm" color="muted">$142.00</Text>
                </VStack>
              </VStack>
            </CardContent>
          </Card>
      </ShowcaseDemoSection>

      {/* In Context - Navigation */}
      <ShowcaseDemoSection
        title="In Context: Navigation Separator"
        description="Vertical dividers separating navigation or action items"
       
      >
          <Card>
            <CardContent>
              <HStack justify="center" align="center" className="h-10">
                <Text size="sm" className="px-4 cursor-pointer hover:text-[#78716C]">Dashboard</Text>
                <Divider orientation="vertical" spacing={0} />
                <Text size="sm" className="px-4 cursor-pointer hover:text-[#78716C]">Policies</Text>
                <Divider orientation="vertical" spacing={0} />
                <Text size="sm" className="px-4 cursor-pointer hover:text-[#78716C]">Claims</Text>
                <Divider orientation="vertical" spacing={0} />
                <Text size="sm" className="px-4 cursor-pointer hover:text-[#78716C]">Documents</Text>
                <Divider orientation="vertical" spacing={0} />
                <Text size="sm" className="px-4 cursor-pointer hover:text-[#78716C]">Settings</Text>
              </HStack>
            </CardContent>
          </Card>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const DividerShowcase: ShowcaseDefinition = {
  id: 'divider',
  name: 'Divider',
  category: 'layout',
  description: 'Visual separator between content sections',
  propsDefinition: [
    {
      name: 'orientation',
      type: 'select',
      options: ['horizontal', 'vertical'],
      defaultValue: 'horizontal',
      description: 'Direction of the divider line',
    },
    {
      name: 'variant',
      type: 'select',
      options: ['solid', 'dashed'],
      defaultValue: 'solid',
      description: 'Line style of the divider',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="separator"',
      'aria-orientation="horizontal|vertical"',
    ],
    keyboardNav: [
      'Dividers are not focusable (decorative)',
    ],
    notes: [
      'Uses semantic <hr> for horizontal dividers without labels',
      'Proper aria-orientation for screen readers',
      'Labels are visually rendered but not required for accessibility',
    ],
  },
  component: DividerShowcaseComponent,
};
